import * as Sentry from '@sentry/react';
import axios from 'axios';
import { WaidAnalytics } from '@curseduca/analytics';
import type { Client } from '@curseduca/analytics';
import { apiFactory } from '../api/ApiFactory';

/**
 * The checkout's door into Waid Analytics. The only file allowed to import
 * `@curseduca/analytics`, the same rule the member area and the admin keep.
 *
 * Which tenant the events belong to is decided by the platform's analytics
 * key pair (`analytics.pk` + `analytics.accountId`), read from the same
 * `/platform` payload the other fronts use. That endpoint answers to the
 * platform api_key alone, which this app already resolves from the URL slug
 * (see usePlatformApiKey) — so no new backend surface is needed. A tenant
 * without the pair simply sends nothing.
 *
 * This host is not the member area's host, so a checkout visit is its own
 * anonymous session: the link back to the member who later pays is made
 * server-side by the confirmed sale, not by this client.
 */

type PlatformAnalytics = {
	analytics?: { pk?: string | null; accountId?: string | null } | null;
};

export type CheckoutOpenInput =
	| { type: 'single'; product: { id: string; name: string } }
	| { type: 'subscription'; plan: { id: string; name: string } };

let client: Promise<Client | null> | null = null;
let clientApiKey = '';

/**
 * One client per platform api_key. The key can change after mount (the
 * slug lookup enables it a tick later), so a cached client for another key
 * is dropped rather than reused.
 */
const resolve = async (): Promise<Client | null> => {
	const apiKey = await apiFactory.getApiKey();
	if (!apiKey) return null;
	if (client && clientApiKey === apiKey) return client;

	clientApiKey = apiKey;
	client = (async () => {
		try {
			const { data } = await axios.get<PlatformAnalytics>(
				`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platform`,
				{ headers: { api_key: apiKey } }
			);
			const publicKey = data.analytics?.pk ?? null;
			const accountID = data.analytics?.accountId ?? null;
			if (!publicKey || !accountID) return null;

			const baseUrl = process.env.REACT_APP_WAID_ANALYTICS_URL;
			const created = WaidAnalytics.create({
				apiKey: publicKey,
				accountID,
				// A checkout is one page: every event is worth a round trip
				// on its own, and a batch window would hold the open until
				// a later event or the flush timer.
				batch: false,
				...(baseUrl ? { baseUrl } : {})
			});
			// The page the session entered on. The SDK's heartbeats hang off
			// it, which is what gives the session its time on page.
			created.view(window.location.pathname);
			return created;
		} catch (error) {
			Sentry.captureException(error, {
				tags: { context: 'waid-analytics:create' }
			});
			return null;
		}
	})();
	return client;
};

/** A checkout page was landed on. Fire once per page load. */
export const trackCheckoutOpen = async (
	input: CheckoutOpenInput
): Promise<void> => {
	const analytics = await resolve();
	if (!analytics) return;
	try {
		await analytics.track({
			kind: 'pay::checkout::open',
			metadata:
				input.type === 'single'
					? { checkout: { type: 'single' }, product: input.product }
					: { checkout: { type: 'subscription' }, plan: input.plan }
		});
	} catch (error) {
		Sentry.captureException(error, {
			tags: {
				context: 'waid-analytics:track',
				kind: 'pay::checkout::open'
			}
		});
	}
};
